import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface BaseDropdownOption {
  id: string;
  label: string;
  emoji: string;
  description: string;
}

interface BaseDropdownProps<T extends BaseDropdownOption> {
  value: string;
  onChange: (value: string) => void;
  options: T[];
  className?: string;
  placeholder?: string;
  minWidth?: string;
}

export function BaseDropdown<T extends BaseDropdownOption>({
  value,
  onChange,
  options,
  className = '',
  placeholder = 'Select...',
  minWidth = 'min-w-[180px]'
}: BaseDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = options.find(option => option.id === value) || options[0];

  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${minWidth} ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
      >
        <span className="flex items-center gap-2">
          <span>{selectedOption?.emoji}</span>
          <span className="text-gray-700">{selectedOption?.label || placeholder}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 w-72 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-[70] overflow-y-auto max-h-96">
            <div className="py-1">
              {options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleSelect(option.id)}
                  className={`group w-full flex flex-col items-start gap-0.5 px-4 py-2.5 text-sm text-left transition-colors ${
                    option.id === value
                      ? 'bg-primary-50'
                      : 'hover:bg-primary-25'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span>{option.emoji}</span>
                    <span className={`${
                      option.id === value 
                        ? 'text-primary-600 font-semibold' 
                        : 'text-gray-800 font-medium group-hover:text-primary-600'
                    }`}>
                      {option.label}
                    </span>
                  </div>
                  <p className={`text-xs pl-6 ${
                    option.id === value 
                      ? 'text-primary-500' 
                      : 'text-gray-500 group-hover:text-primary-400'
                  }`}>
                    {option.description}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
} 